import type { QuoteListParams, QuoteStatus } from '@/api/types'
import { QUOTE_STATUS_ORDER } from '@/lib/orders'
import { csvOf, flag, integer, orElse, readSearch, text } from '@/lib/search-params'

const STATUS_SET = new Set<string>(QUOTE_STATUS_ORDER)

/**
 * Filtres de la liste des soumissions, portés par l'URL : « les soumissions
 * envoyées d'ACME qui expirent avant la fin du mois » est un lien qu'on s'envoie.
 * `statut` est une liste séparée par des virgules, comme le contrat l'accepte.
 */
const SHAPE = {
  q: text(),
  client: text(),
  affaire: text(),
  /** Statuts séparés par des virgules ; ce qui n'est pas connu tombe. */
  statut: csvOf(STATUS_SET),
  avant: text(),
  apres: text(),
  /** La corbeille : les seules soumissions supprimées. */
  corbeille: flag(),
  page: orElse(integer({ min: 1 }), 1),
}

export type QuotesSearch = ReturnType<typeof parseQuotesSearch>

export function parseQuotesSearch(search: Record<string, unknown>) {
  return readSearch(SHAPE, search)
}

export const EMPTY_QUOTE_FILTERS: Partial<QuotesSearch> = {
  q: undefined,
  client: undefined,
  affaire: undefined,
  statut: undefined,
  avant: undefined,
  apres: undefined,
  corbeille: undefined,
  page: 1,
}

export function selectedStatuses(search: QuotesSearch): QuoteStatus[] {
  return (search.statut?.split(',') ?? []).filter((status): status is QuoteStatus =>
    STATUS_SET.has(status),
  )
}

export function hasActiveQuoteFilters(search: QuotesSearch): boolean {
  return !!(
    search.q ??
    search.client ??
    search.affaire ??
    search.statut ??
    search.avant ??
    search.apres ??
    search.corbeille
  )
}

export function toQuoteListParams(search: QuotesSearch, pageSize: number): QuoteListParams {
  const q = search.q?.trim()
  return {
    q: q === '' ? undefined : q,
    client_id: search.client,
    opportunity_id: search.affaire,
    status: search.statut,
    valid_before: search.avant,
    valid_after: search.apres,
    deleted: search.corbeille ? true : undefined,
    page: search.page,
    page_size: pageSize,
  }
}
